import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import dataRoomApi from '../../dataRoomApi';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { useTranslation } from 'react-i18next';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import {
  CheckDeleteFilesResponse,
  CheckDeleteShareableLink,
} from '../../models/DataRoomNode';
import { getArrayLength, getHasElementsInArray } from 'utils';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';

import './DeleteFilesModal.styles.scss';

type DeleteFilesModalProps = {
  className?: string;
  nodes: string[];
  type: string;
  name?: string;
  onDelete: () => Promise<void>;
} & ModalProps;

const DeleteFilesModal: React.ForwardRefRenderFunction<
  ModalRef,
  DeleteFilesModalProps
> = (props, ref) => {
  const { className, type, name, nodes, onDelete, ...rest } = props;

  const [data, setData] = useState<CheckDeleteFilesResponse>(null);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);

  const { emptyShareableLinks, affectedShareableLinks } = data || {};

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('delete-files-modal', className);

  const { t } = useTranslation();

  const [checkDeleteFiles, isLoading] = useCallbackAsync(async () => {
    const { data } = await dataRoomApi.nodes.checkDeleteFiles(_id, nodes);

    setData(data);
  });

  const [deleteNodes, isDeleting] = useCallbackAsync(onDelete);

  const count = useMemo(() => getArrayLength(nodes), [nodes]);

  const hasEmptySl = useMemo(
    () => getHasElementsInArray(emptyShareableLinks),
    [emptyShareableLinks],
  );

  const hasAffectedSl = useMemo(
    () => getHasElementsInArray(affectedShareableLinks),
    [affectedShareableLinks],
  );

  const showAffectedSl = useMemo(
    () => !isLoading && (hasAffectedSl || hasEmptySl),
    [isLoading, hasAffectedSl, hasEmptySl],
  );

  const renderSecureLinkSection = useCallback(
    (
      title: string,
      description: string,
      links: CheckDeleteShareableLink[],
      className?: string,
    ) => (
      <div className={classNames('secure-links__section', className)}>
        <p className="title">{t(title)}</p>
        <p>{t(description)}</p>

        <ol>
          {links.map(({ _id, name, remainingFilesCount }) => (
            <li key={_id}>
              {remainingFilesCount
                ? t('secureLinkRemainingFiles', {
                    name,
                    count: remainingFilesCount,
                  })
                : name}
            </li>
          ))}
        </ol>
      </div>
    ),
    [t],
  );

  const onClose = useCallback(() => setData(null), []);

  useEffect(() => {
    if (!modal?.openState) return;

    checkDeleteFiles();
  }, [modal?.openState, checkDeleteFiles]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={
        <div className="delete-files-modal__title">
          <WarningExclamationIcon />
          {t(
            showAffectedSl ? 'thisActionWillAffectSecureLinks' : 'deleteNodes',
            { count, type },
          )}
        </div>
      }
      onClose={onClose}
      {...rest}
    >
      {(isLoading || isDeleting) && <Loader />}

      {!isLoading && (
        <p>
          {t(
            showAffectedSl
              ? hasEmptySl
                ? 'deleteFilesConfirmationEmpty'
                : 'deleteFilesConfirmationAffect'
              : 'deleteFilesConfirmation',
            { count, type, name },
          )}
        </p>
      )}

      {showAffectedSl && (
        <div className="secure-links">
          {hasEmptySl &&
            renderSecureLinkSection(
              'secureLinksToBeDeactivated',
              'theseLinksWillHaveNoFilesRemaining',
              emptyShareableLinks,
              'secure-links__section--empty',
            )}

          {hasAffectedSl &&
            renderSecureLinkSection(
              'otherAffectedSecureLinks',
              'theseLinksWillRemainActive',
              affectedShareableLinks,
              'secure-links__section--affected',
            )}
        </div>
      )}

      <div className="actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>
        <Button className="alert" disabled={isDeleting} onClick={deleteNodes}>
          {t('deleteNodes', { count, type })}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(DeleteFilesModal);
